import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

const stats = [
  { key: 'patients', value: 500000, suffix: '+' },
  { key: 'tests', value: 1500, suffix: '+' },
  { key: 'branches', value: 3, suffix: '+' },
  { key: 'accuracy', value: 99, suffix: '%' },
];

const Statistics = () => {
  const { t } = useContent();
  const sectionRef = useRef<HTMLDivElement>(null);
  const statRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || hasAnimated) return;

    const scrollTrigger = ScrollTrigger.create({
      trigger: section,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        setHasAnimated(true);
        
        stats.forEach((stat, index) => {
          const element = statRefs.current[index];
          if (!element) return;

          const obj = { value: 0 };
          
          gsap.to(obj, {
            value: stat.value,
            duration: 2.5,
            ease: 'power2.out',
            onUpdate: function () {
              const currentValue = Math.floor(obj.value);
              
              // Format numbers with commas for large values
              const formattedValue = currentValue.toLocaleString('en-US');
              element.textContent = formattedValue + stat.suffix;
            },
            delay: index * 0.1, // Stagger animation for each stat
          });
        });
      },
    });

    return () => {
      scrollTrigger.kill();
    };
  }, [hasAnimated]);

  const formatInitialValue = (suffix: string) => {
    return '0' + suffix;
  };

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-brand-purple-dark dark:bg-dark-primary"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            {t('home.statistics.title')}
          </h2>
          <p className="text-lg text-white/80">
            {t('home.statistics.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 bg-white/10 dark:bg-dark-secondary/50 rounded-xl backdrop-blur-md hover:bg-white/15 dark:hover:bg-dark-secondary/70 transition-all duration-300"
            >
              <div
                ref={(el) => {
                  statRefs.current[index] = el;
                }}
                className="text-5xl md:text-6xl font-bold mb-2 text-brand-orange-base tabular-nums"
              >
                {formatInitialValue(stat.suffix)}
              </div>
              <div className="text-lg text-white/90">
                {stat.key === 'patients' && t('home.statistics.patientsServed')}
                {stat.key === 'tests' && t('home.statistics.testsAvailable')}
                {stat.key === 'branches' && t('home.statistics.branchesCount')}
                {stat.key === 'accuracy' && t('home.statistics.accuracyRate')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Statistics;

